
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';

const Register: React.FC = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    countryCode: '+1',
    phoneNumber: '',
    captchaInput: '',
    acceptTerms: false
  });

  const [captchaCode, setCaptchaCode] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const generateCaptcha = useCallback(() => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let result = '';
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptchaCode(result);
  }, []);

  useEffect(() => {
    generateCaptcha();
  }, [generateCaptcha]);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName) newErrors.firstName = 'Required';
    if (!formData.lastName) newErrors.lastName = 'Required';

    if (!formData.email) {
      newErrors.email = 'Email required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Invalid format';
    }

    if (!formData.phoneNumber) newErrors.phoneNumber = 'Required';

    if (!formData.password) {
      newErrors.password = 'Required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Min 8 chars';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Mismatch';
    }

    if (formData.captchaInput.toUpperCase() !== captchaCode) {
      newErrors.captchaInput = 'Security code mismatch';
      generateCaptcha();
    }

    if (!formData.acceptTerms) {
      newErrors.acceptTerms = 'Terms must be accepted';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsLoading(true);
    // Simulate premium registration    
    const res = await register({
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phoneNumber, // Corrected from formData.phone
      password: formData.password
    });

    if (res.success) {
      navigate('/login');
    }

    setIsLoading(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors(prev => {
        const n = { ...prev };
        delete n[name];
        return n;
      });
    }
  };

  if (isSuccess) {
    return (
      <div className="py-12 text-center space-y-6">
        <div className="relative w-24 h-24 mx-auto">
          <div className="absolute inset-0 bg-brand-primary/20 rounded-full"></div>
          <div className="relative w-24 h-24 bg-brand-primary rounded-full flex items-center justify-center border-4 border-brand-dark shadow-[0_0_30px_rgba(255,206,27,0.4)]">
            <svg className="w-12 h-12 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>
        <div className="space-y-2">
          <h2 className="text-2xl font-bold text-white tracking-tight">Identity Registered</h2>
          <p className="text-brand-muted text-sm px-6 leading-relaxed">
            Your application for membership is being processed by the Nexus protocol.
            Redirecting to secure terminal...
          </p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-1 mb-2">
        <h2 className="text-xl font-bold text-white tracking-tight uppercase">Agency Enrollment</h2>
        <p className="text-xs text-brand-muted uppercase tracking-widest font-medium opacity-60">Step 1: Identity Protocol</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Input
          label="First Name"
          name="firstName"
          placeholder="First"
          value={formData.firstName}
          onChange={handleChange}
          error={errors.firstName}
          required
        />
        <Input
          label="Last Name"
          name="lastName"
          placeholder="Last"
          value={formData.lastName}
          onChange={handleChange}
          error={errors.lastName}
          required
        />
      </div>

      <Input
        label="Email Address"
        name="email"
        type="email"
        placeholder="e.g. trader@coinnexs.com"
        value={formData.email}
        onChange={handleChange}
        error={errors.email}
        required
      />

      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-1">
          <label className="text-xs font-bold text-brand-muted uppercase tracking-wider ml-1 mb-1.5 block">Code</label>
          <input
            name="countryCode"
            value={formData.countryCode}
            onChange={handleChange}
            placeholder="+1"
            className="w-full bg-brand-dark/50 border border-brand-border rounded-xl px-3 py-3 text-white focus:outline-none focus:border-brand-primary transition-all text-sm font-mono"
          />
        </div>
        <div className="col-span-2">
          <Input
            label="Phone Number"
            name="phoneNumber"
            placeholder="Main line"
            value={formData.phoneNumber}
            onChange={handleChange}
            error={errors.phoneNumber}
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Input
          label="Access Key"
          name="password"
          type="password"
          placeholder="8+ chars"
          value={formData.password}
          onChange={handleChange}
          error={errors.password}
          required
        />
        <Input
          label="Confirm"
          name="confirmPassword"
          type="password"
          placeholder="Repeat"
          value={formData.confirmPassword}
          onChange={handleChange}
          error={errors.confirmPassword}
          required
        />
      </div>

      {/* Security Captcha Section */}
      <div className="bg-brand-dark/40 border border-brand-border/50 rounded-2xl p-4 space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-[10px] font-bold text-brand-muted uppercase tracking-[0.2em]">Security Verification</label>
          <button
            type="button"
            onClick={generateCaptcha}
            className="text-[10px] text-brand-primary font-bold hover:underline"
          >
            REFRESH CODE
          </button>
        </div>

        <div className="flex flex-col gap-3">
          <div className="w-full h-12 bg-black/80 rounded-xl border border-brand-border flex items-center justify-center select-none overflow-hidden relative">
            {/* Captcha Background Noise Lines */}
            <div className="absolute inset-0 opacity-20 pointer-events-none">
              <div className="absolute top-1/2 left-0 w-full h-[1px] bg-brand-primary rotate-12"></div>
              <div className="absolute top-1/2 left-0 w-full h-[1px] bg-brand-primary -rotate-12"></div>
            </div>
            <span className="text-xl font-mono font-black text-brand-primary tracking-[0.4em] italic drop-shadow-[0_0_8px_rgba(255,206,27,0.4)]">
              {captchaCode}
            </span>
          </div>
          <div className="w-full">
            <input
              name="captchaInput"
              placeholder="Enter Code Above"
              value={formData.captchaInput}
              onChange={handleChange}
              className={`w-full h-12 bg-brand-dark/50 border ${errors.captchaInput ? 'border-red-500' : 'border-brand-border'} rounded-xl px-4 text-white focus:outline-none focus:border-brand-primary transition-all text-center font-bold tracking-widest placeholder:text-brand-muted/30 placeholder:tracking-normal`}
            />
          </div>
        </div>
        {errors.captchaInput && <p className="text-[10px] text-red-400 font-medium text-center">{errors.captchaInput}</p>}
      </div>

      <div className="space-y-3 pt-2">
        <label className="flex items-start gap-3 cursor-pointer group">
          <div className="relative flex items-center justify-center mt-0.5 shrink-0">
            <input
              name="acceptTerms"
              type="checkbox"
              className={`peer appearance-none w-5 h-5 rounded border bg-brand-dark/50 transition-all duration-200 ${errors.acceptTerms ? 'border-red-500/50' : 'border-brand-border'} checked:bg-brand-primary checked:border-brand-primary`}
              checked={formData.acceptTerms}
              onChange={handleChange}
            />
            <svg className="absolute w-3.5 h-3.5 text-black opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div className="space-y-0.5">
            <span className="text-[10px] text-brand-muted group-hover:text-brand-text transition-colors leading-relaxed block uppercase font-medium">
              I certify my adherence to the <span className="text-brand-primary underline">Operational protocols</span> and <span className="text-brand-primary underline">Security standards</span>.
            </span>
            {errors.acceptTerms && <p className="text-[10px] text-red-400 font-medium">{errors.acceptTerms}</p>}
          </div>
        </label>

        <Button type="submit" className="w-full h-14" isLoading={isLoading}>
          ENROLL IN NEXUS
        </Button>
      </div>

      <p className="text-center text-[10px] text-brand-muted uppercase tracking-widest font-bold">
        Already a member? <span className="text-brand-primary cursor-pointer hover:underline" onClick={() => navigate('/login')}>Secure Login</span>
      </p>
    </form>
  );
};

export default Register;
